import CONFIG from 'config';
import RCSLoggerFactory from 'rcs_logger';

/**
 * RCS Logger
 */
export const logger = RCSLoggerFactory.init('RCS_TAG-MANAGER', {
    level: CONFIG.build.env === 'prod' ? 'error' : 'debug',
});

/**
 * @param {string[]} targets
 * @returns { boolean }
 */
export const checkOnElementsPresence = (targets = []) => {
    const selectors = targets.reduce((acc, value, index) => `${acc}${index === 0 ? '' : ','} ${value}`, '');
    if (!selectors) return false;
    const elements = document.querySelectorAll(selectors);
    return elements && elements.length > 0;
};

/**
 * @param { string } parameter
 * @returns { string | false }
 */
const getQueryString = (parameter) => {
    const loc = location.search.substring(1, location.search.length);
    let paramValue = '';
    const params = loc.split('&');
    params.forEach((item) => {
        const currentParamValue = item.substring(0, item.indexOf('='));
        if (currentParamValue === parameter) {
            paramValue = item.substring(item.indexOf('=') + 1);
        }
    });
    return paramValue || false;
};

/**
 * @param {HTMLAnchorElement} a - link element
 * @param {string} qs - query string to add to the link url
 * @param {boolean} addNoFollow - if true, adds rel="nofollow" to the link
 */
const updateLinkUrl = (a, qs, addNoFollow) => {
    let href = a.getAttribute('href');
    const anchorId = href.indexOf('#') > -1 ? href.substring(href.indexOf('#')) : '';
    href = href.replace(anchorId, '').trim();
    a.setAttribute('href', href + (href.indexOf('?') > -1 ? '&' : '?') + qs + anchorId);
    if (addNoFollow) {
        a.setAttribute('rel', 'nofollow');
    }
};

/**
 * @param { string } url
 * @returns { boolean }
 */
const isExceptionUrl = (url) => {
    const exceptionUrls = CONFIG.linkUrlParamListener.exceptionUrls || [];
    const exceptionDomains = CONFIG.linkUrlParamListener.exceptionDomains || [];
    try {
        if (url.startsWith('/')) {
            const cleanedUrl = url.split('?')[0];
            return exceptionUrls.includes(cleanedUrl);
        }

        const { origin, pathname } = new URL(url);
        return exceptionDomains.includes(origin) || exceptionUrls.includes(pathname);
    } catch (_) {
        return false;
    }
};

/**
 * @param { string[] } propagetedQSParams
 * @returns { void }
 */
export const overridePageLinks = (propagetedQSParams) => {
    const detectedParams = propagetedQSParams.map((param) => ({ name: param, value: getQueryString(param) })).filter((e) => e.value);

    if (!detectedParams.length) return;

    const isAppV2Exception = Boolean(detectedParams.find((param) => param.name === 'app_v2' && param.value === 'true'));

    const qsStringToAdd = detectedParams.map((paramObj) => paramObj.name + '=' + paramObj.value).join('&');

    setInterval(() => {
        const links = Array.from(document.querySelectorAll('a:not([data-link-override])'));

        links.forEach((a) => {
            const href = a.getAttribute('href');
            if (href && (href.startsWith('http') || href.startsWith('/')) && !isExceptionUrl(href)) {
                updateLinkUrl(/** @type {HTMLAnchorElement} */ (a), qsStringToAdd, isAppV2Exception);
                a.setAttribute('data-link-override', 'true');
            } else {
                a.setAttribute('data-link-override', 'false');
            }
        });
    }, 500);
};

/**
 * Waits until a predicate becomes true, checking every [delta]ms, until [timeCap]ms have passed.
 * @param { Object } param The parameters.
 * @param { () => boolean } param.predicate The predicate function to check.
 * @param { number } [param.delta] The delta time in milliseconds.
 * @param { number } [param.timeCap] The maximum wait time in milliseconds.
 * @returns { Promise<boolean> } Promise resolving to true if predicate became true, false if timed out.
 */
export const waitUntil = ({ predicate, delta = 200, timeCap = 1000 }) => {
    if (predicate()) return Promise.resolve(true);

    return new Promise((resolve) => {
        let times = 1;

        const interval = setInterval(() => {
            if (predicate()) {
                clearInterval(interval);
                resolve(true);
            } else if (timeCap && times * delta >= timeCap) {
                clearInterval(interval);
                resolve(false);
            }

            times++;
        }, delta);
    });
};
